import { GatewayError } from '../gateway/GatewayClient';

export type LegacyLoginRejection = Readonly<{
  inlineMessage: string;
  modal?: Readonly<{
    title?: string;
    message: string;
    reloadOnDismiss: boolean;
  }>;
}>;

const OLD_CLIENT_MESSAGE =
  'We have detected that you are attempting to log in to Nord with a client that is older than the version on the server. Nord will shut down. Please download and start the latest Nord client, then log in normally.';
const SERVER_MAINTENANCE_MESSAGE =
  'The Nord server is currently down for maintenance. Please return shortly!';

export function legacyLoginRejection(error: unknown): LegacyLoginRejection {
  if (!(error instanceof GatewayError)) {
    return {
      inlineMessage:
        error instanceof Error && error.message.length > 0
          ? error.message
          : 'Nord could not log you in.',
    };
  }
  switch (error.code) {
    case 'invalid_credentials':
      return { inlineMessage: 'Failed to log in.' };
    case 'account_blocked': {
      const savedReason = error.blockReason?.trim() ?? '';
      const reason = savedReason.length > 0 ? savedReason : 'No reason provided.';
      const expiry =
        error.blockedUntil === undefined
          ? 'Not provided.'
          : error.blockedUntil === 0
            ? 'Permanent'
            : formatBlockedUntil(error.blockedUntil);
      const message = `Your login is blocked by a ban.\n\nReason: ${reason}\n\nExpires: ${expiry}`;
      return {
        inlineMessage: '',
        modal: { title: 'Ban', message, reloadOnDismiss: true },
      };
    }
    case 'client_version_unsupported':
      return {
        inlineMessage: '',
        modal: {
          title: 'Version error',
          message: OLD_CLIENT_MESSAGE,
          reloadOnDismiss: true,
        },
      };
    case 'maintenance_mode':
    case 'server_full':
      return {
        inlineMessage: 'Failed to log in.',
        modal: {
          message: SERVER_MAINTENANCE_MESSAGE,
          reloadOnDismiss: false,
        },
      };
    case 'gateway_unreachable':
    case 'legacy_timeout':
    case 'legacy_unavailable':
      return { inlineMessage: 'Failed to connect to Nord.' };
    default:
      return { inlineMessage: error.message || 'Nord could not log you in.' };
  }
}

function formatBlockedUntil(timestamp: number): string {
  const formatted = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: 'Europe/Stockholm',
  }).format(new Date(timestamp));
  return `${formatted} (Stockholm time)`;
}
